import React, { useCallback, useState, useContext, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { AppContext } from '../ThemeLangContext';
import JSZip from 'jszip';

import {
    Container, Paper, Typography, Box, Grid,
    IconButton, Button, Switch,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Tooltip, Chip, Snackbar, Alert
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorIcon from '@mui/icons-material/Error';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LanguageIcon from '@mui/icons-material/Language';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import SecurityIcon from '@mui/icons-material/Security';

import logo from '../logo.svg';

const translations = {
    uk: {
        title: 'Модифікатор OpenVPN',
        subtitle: 'Зберіть всі сертифікати та ключі в один зручний .ovpn файл',
        dropText: 'Перетягніть файли (або ZIP-архів) сюди',
        acceptTypes: '(Підтримуються: .zip, .ovpn, .crt, .key, .pem)',
        reqSection: 'Обов\'язкові',
        optSection: 'Додаткові',
        downloadBtn: 'Завантажити',
        previewBtn: 'Перегляд',
        copyBtn: 'Копіювати',
        resetBtn: 'Очистити все',
        fileNameLabel: 'Ім\'я готового файлу',
        copied: 'Скопійовано!',
        privacyText: 'Усі дані обробляються локально у браузері. Ключі не відправляються на сервер.',
        fileNames: {
            ovpn: 'Базовий конфіг (.ovpn)',
            ca: 'CA сертифікат (ca.crt)',
            cert: 'Клієнтський сертифікат (.crt)',
            key: 'Приватний ключ (.key)',
            dh: 'Параметри DH (.pem)',
            ta: 'Ключ TLS Auth (ta.key)'
        },
        noFile: 'Очікування файлу...',
        configInfo: 'Аналіз конфігу:',
        notClient: 'Увага: Не знайдено директиву "client". Це може бути серверний профіль.',
        zipSuccess: 'Успіх! Розпаковано файлів: ',
        zipEmpty: 'В архіві не знайдено підходящих OpenVPN файлів.'
    },
    en: {
        title: 'OpenVPN Config Modifier',
        subtitle: 'Combine all certs and keys into a single .ovpn file',
        dropText: 'Drag and drop files (or a ZIP archive) here',
        acceptTypes: '(Supported: .zip, .ovpn, .crt, .key, .pem)',
        reqSection: 'Required',
        optSection: 'Optional',
        downloadBtn: 'Download',
        previewBtn: 'Preview',
        copyBtn: 'Copy',
        resetBtn: 'Reset All',
        fileNameLabel: 'Output File Name',
        copied: 'Copied!',
        privacyText: 'All processing happens locally in your browser. Keys are never sent to a server.',
        fileNames: {
            ovpn: 'Base Config (.ovpn)',
            ca: 'CA Certificate (ca.crt)',
            cert: 'Client Certificate (.crt)',
            key: 'Private Key (.key)',
            dh: 'DH Parameters (.pem)',
            ta: 'TLS Auth Key (ta.key)'
        },
        noFile: 'Waiting for file...',
        configInfo: 'Config Analysis:',
        notClient: 'Warning: "client" directive not found. This might be a server profile.',
        zipSuccess: 'Success! Extracted files: ',
        zipEmpty: 'No relevant OpenVPN files found in the archive.'
    }
};

export function MyDropzone() {
    const { mode, toggleTheme, lang, toggleLang } = useContext(AppContext);
    const t = translations[lang];

    const [files, setFiles] = useState({
        ovpn: null, ca: null, cert: null, key: null, dh: null, ta: null,
    });
    
    const [includeDh, setIncludeDh] = useState(false);
    const [customFileName, setCustomFileName] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [generatedConfig, setGeneratedConfig] = useState('');
    const [copied, setCopied] = useState(false);
    const [configInfo, setConfigInfo] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    const showToast = (message, severity = 'info') => {
        setToast({ open: true, message, severity });
    };

    const handleCloseToast = (event, reason) => {
        if (reason === 'clickaway') return;
        setToast(prev => ({ ...prev, open: false }));
    };

    const parseConfigInfo = (contents) => {
        const lines = contents.split(/\r?\n/);
        let proto = 'UDP'; 
        let remote = '';
        let cipher = '';
        let port = '1194';
        let isClient = false;

        for (let line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith('#') || trimmed.startsWith(';')) continue;
            
            const parts = trimmed.split(/\s+/);
            const dir = parts[0].toLowerCase();

            if (dir === 'client') isClient = true;
            if (dir === 'proto' && parts[1]) proto = parts[1].toUpperCase();
            if (dir === 'remote' && parts[1]) {
                remote = parts[1];
                if (parts[2]) port = parts[2];
            }
            if (dir === 'cipher' && parts[1]) cipher = parts[1].toUpperCase();
        }
        
        if (remote || isClient) {
            setConfigInfo({ proto, remote, port, cipher, isClient });
        } else {
            setConfigInfo(null);
        }
    };

    useEffect(() => {
        if (files.ovpn && !customFileName) {
            setCustomFileName('snet_' + files.ovpn.name);
            parseConfigInfo(files.ovpn.contents);
        }
        if (!files.ovpn) {
            setCustomFileName('');
            setConfigInfo(null);
        }
    }, [files.ovpn]); // eslint-disable-line react-hooks/exhaustive-deps

    const processFileContent = (name, contents) => {
        const ext = name.split('.').pop().toLowerCase();
        const fName = name.toLowerCase();

        let type = null;
        if (ext === 'ovpn' || ext === 'conf') type = 'ovpn';
        else if (fName === 'ca.crt' || fName === 'ca.pem') type = 'ca';
        else if (fName === 'ta.key' || fName === 'tls-auth.key' || fName === 'tc.key') type = 'ta';
        else if (ext === 'crt') type = 'cert';
        else if (ext === 'key') type = 'key';
        else if (ext === 'pem') type = 'dh';

        if (type) {
            setFiles(prev => ({ ...prev, [type]: { name: name, contents } }));
            return true;
        }
        return false;
    };

    const [hasDropped, setHasDropped] = useState(false);

    const onDrop = useCallback(async (acceptedFiles) => {
        setHasDropped(true);
        for (const file of acceptedFiles) {
            const ext = file.name.split('.').pop().toLowerCase();
            
            if (ext === 'zip') {
                const zip = new JSZip();
                try {
                    const loadedZip = await zip.loadAsync(file);
                    let extractedCount = 0;
                    for (const relativePath in loadedZip.files) {
                        const zipEntry = loadedZip.files[relativePath];
                        if (!zipEntry.dir) {
                            const contents = await zipEntry.async("string");
                            const name = zipEntry.name.split('/').pop(); 
                            if (processFileContent(name, contents)) {
                                extractedCount++;
                            }
                        }
                    }
                    if (extractedCount > 0) {
                        showToast(t.zipSuccess + extractedCount, 'success');
                    } else {
                        showToast(t.zipEmpty, 'warning');
                    }
                } catch (e) {
                    console.error("Error reading zip", e);
                    showToast('Помилка читання ZIP-архіву', 'error');
                }
            } else {
                const reader = new FileReader();
                reader.onload = (e) => processFileContent(file.name, e.target.result);
                reader.readAsText(file);
            }
        }
    }, [t]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        maxFiles: 20,
        accept: {
            'application/x-openvpn-profile': ['.ovpn', '.conf'],
            'application/x-pem-file': ['.pem', '.crt', '.key'],
            'application/zip': ['.zip'],
            'application/x-zip-compressed': ['.zip']
        },
        onDrop
    });

    const removeFile = (type) => setFiles(prev => ({ ...prev, [type]: null }));
    const resetAll = () => {
        setFiles({ ovpn: null, ca: null, cert: null, key: null, dh: null, ta: null });
        setIncludeDh(false);
        setCustomFileName('');
        setGeneratedConfig('');
        setConfigInfo(null);
        setHasDropped(false);
    };

    const buildConfig = () => {
        if (!files.ovpn) return '';

        const lines = files.ovpn.contents.split(/\r?\n/);
        const newLines = [];
        const directivesToRemove = ['ca', 'cert', 'key', 'dh', 'tls-auth', 'tls-crypt'];
        
        let hasTlsCrypt = false;
        let hasKeyDirection = false;
        let keyDirectionValue = '1'; 

        for (let line of lines) {
            const trimmed = line.trim();
            if (trimmed === '') continue;
            
            if (trimmed.startsWith('#') || trimmed.startsWith(';')) {
                newLines.push(line);
                continue;
            }
            
            const parts = trimmed.split(/\s+/);
            const directive = parts[0].toLowerCase();
            
            if (directive === 'tls-crypt') hasTlsCrypt = true;
            if (directive === 'key-direction') hasKeyDirection = true;

            if (directivesToRemove.includes(directive)) {
                if (!directive.startsWith('<')) {
                    if (directive === 'tls-auth' && parts.length > 2) {
                        keyDirectionValue = parts[2];
                    }
                    continue; 
                }
            }
            newLines.push(line);
        }

        let combined = newLines.join('\n') + '\n\n';

        if (files.ca) combined += '<ca>\n' + files.ca.contents.trim() + '\n</ca>\n\n';
        if (files.cert) combined += '<cert>\n' + files.cert.contents.trim() + '\n</cert>\n\n';
        if (files.key) combined += '<key>\n' + files.key.contents.trim() + '\n</key>\n\n';
        
        if (files.dh && includeDh) combined += '<dh>\n' + files.dh.contents.trim() + '\n</dh>\n\n';
        
        if (files.ta) {
            const tag = hasTlsCrypt ? 'tls-crypt' : 'tls-auth';
            if (tag === 'tls-auth' && !hasKeyDirection) {
                combined += `key-direction ${keyDirectionValue}\n`;
            }
            combined += `<${tag}>\n` + files.ta.contents.trim() + `\n</${tag}>\n\n`;
        }

        return combined;
    };

    const handlePreview = () => {
        const config = buildConfig();
        setGeneratedConfig(config);
        setPreviewOpen(true);
    };

    const handleDownload = () => {
        const config = generatedConfig || buildConfig();
        const blob = new Blob([config], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const outName = customFileName.endsWith('.ovpn') ? customFileName : customFileName + '.ovpn';
        link.download = outName || 'snet_config.ovpn';
        link.click();
        setPreviewOpen(false);
        showToast('Конфіг успішно збережено!', 'success');
    };

    const handleCopy = () => {
        const config = generatedConfig || buildConfig();
        navigator.clipboard.writeText(config);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const isReady = files.ovpn && files.ca && files.cert && files.key;

    const FileRow = ({ type, isRequired }) => {
        const file = files[type];
        const isDh = type === 'dh';
        const isError = hasDropped && isRequired && !file;

        return (
            <Box sx={{ 
                display: 'flex', alignItems: 'center', p: {xs: 1, md: 1.25}, mb: {xs: 1, md: 0.5}, 
                borderRadius: 2, 
                bgcolor: file ? (mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)') : (isError ? (mode === 'dark' ? 'rgba(239, 68, 68, 0.05)' : 'rgba(239, 68, 68, 0.02)') : 'transparent'),
                border: '1px solid',
                borderColor: file ? 'divider' : (isError ? 'error.main' : 'transparent'),
                transition: 'all 0.2s',
                '&:hover': { bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' }
            }}>
                {file ? 
                    <CheckCircleIcon sx={{ color: 'success.main', mr: 1.5, fontSize: 20 }} /> : 
                    (isError ? <ErrorIcon sx={{ color: 'error.main', mr: 1.5, fontSize: 20 }} /> : <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', mr: 1.5, fontSize: 20 }} />)
                }
                
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: file ? 'text.primary' : (isError ? 'error.main' : 'text.secondary'), fontSize: '0.85rem' }}>
                            {t.fileNames[type]}
                        </Typography>
                        {isError && <Chip size="small" label={lang === 'uk' ? 'Відсутній' : 'Missing'} sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'error.main', color: '#fff', fontWeight: 'bold' }} />}
                    </Box>
                    <Typography variant="caption" sx={{ color: file ? 'text.secondary' : 'text.disabled', display: 'block', mt: 0.1, fontSize: '0.7rem' }}>
                        {file ? file.name : t.noFile}
                    </Typography>
                    
                    {isDh && file && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
                            <Switch checked={includeDh} onChange={(e) => setIncludeDh(e.target.checked)} size="small" />
                            <Typography variant="caption" sx={{ color: includeDh ? 'warning.main' : 'text.secondary', fontWeight: includeDh ? 'bold' : 'normal', fontSize: '0.7rem' }}>
                                Включати DH (Не рекомендується для Mac/iOS)
                            </Typography>
                        </Box>
                    )}
                </Box>

                {file && (
                    <IconButton size="small" onClick={() => removeFile(type)} sx={{ color: 'text.secondary', ml: 1, '&:hover': { color: 'error.main' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ 
            minHeight: '100vh', display: 'flex', flexDirection: 'column',
            bgcolor: 'background.default',
            position: 'relative',
            px: { xs: 1, sm: 2, md: 4 },
            pt: { xs: 1.5, sm: 2, md: 2 },
            pb: { xs: 2, md: 2 }
        }}>
            <Container maxWidth="md" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', px: {xs: 0, sm: 2} }}>
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: {xs: 1.5, md: 1.5}, pr: {xs: 1, sm: 0} }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={toggleLang}>
                        <LanguageIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="button" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'text.secondary' }}>{lang}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LightModeIcon fontSize="small" sx={{ mr: 0.5, color: mode === 'light' ? 'primary.main' : 'text.disabled' }} />
                        <Switch checked={mode === 'dark'} onChange={toggleTheme} size="small" color="primary" />
                        <DarkModeIcon fontSize="small" sx={{ ml: 0.5, color: mode === 'dark' ? 'primary.main' : 'text.disabled' }} />
                    </Box>
                </Box>

                <Paper elevation={0} sx={{ 
                    borderRadius: {xs: 3, sm: 4}, 
                    border: '1px solid', borderColor: 'divider',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    display: 'flex', flexDirection: 'column'
                }}>
                    <Box sx={{ 
                        py: {xs: 2, md: 2.5}, px: {xs: 2, md: 4}, 
                        borderBottom: '1px solid', borderColor: 'divider', 
                        bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                        display: 'flex', flexDirection: {xs: 'column', sm: 'row'},
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: {xs: 2, md: 3},
                        textAlign: {xs: 'center', sm: 'left'}
                    }}>
                        <Box sx={{ 
                            height: {xs: 56, sm: 64, md: 68}, 
                            width: {xs: 56, sm: 64, md: 68}, 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            filter: mode === 'dark' ? 'drop-shadow(0 0 15px rgba(56, 189, 248, 0.3))' : 'none'
                        }}>
                            <img 
                                src={logo} 
                                alt="SkyNet Logo" 
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                            />
                        </Box>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'center', 
                            height: {sm: 64, md: 68} 
                        }}>
                            <Typography variant="h4" fontWeight="800" sx={{ letterSpacing: '-0.5px', lineHeight: 1.1, mb: {xs: 0.5, sm: 'auto'}, fontSize: {xs: '1.4rem', sm: '1.6rem', md: '1.8rem'} }}>
                                {t.title}
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.2, fontSize: {xs: '0.8rem', sm: '0.85rem', md: '0.9rem'} }}>
                                {t.subtitle}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Privacy Badge */}
                    <Box sx={{ 
                        bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
                        py: {xs: 1, md: 1}, px: {xs: 2, md: 3}, 
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                        borderBottom: '1px solid', borderColor: 'divider',
                        color: 'text.secondary'
                    }}>
                        <SecurityIcon sx={{ fontSize: {xs: 14, md: 16}, opacity: 0.7 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: {xs: '0.75rem', sm: '0.8rem'}, textAlign: 'center' }}>
                            {t.privacyText}
                        </Typography>
                    </Box>

                    <Box sx={{ p: {xs: 2, sm: 3, md: 3}, pb: {xs: 0, md: 0} }}>
                        <Paper
                            {...getRootProps()}
                            elevation={0}
                            sx={{
                                py: {xs: 4, sm: 4, md: 4}, px: {xs: 2, md: 4}, 
                                mb: {xs: 3, md: 2.5}, textAlign: 'center', cursor: 'pointer', 
                                border: '2px dashed',
                                borderColor: isDragActive ? 'primary.main' : 'divider',
                                bgcolor: isDragActive ? (mode === 'dark' ? 'rgba(56, 189, 248, 0.05)' : 'rgba(14, 165, 233, 0.03)') : 'transparent',
                                borderRadius: 3,
                                transition: 'all 0.2s ease',
                                '&:hover': { borderColor: 'primary.main', bgcolor: mode === 'dark' ? 'rgba(56, 189, 248, 0.03)' : 'rgba(14, 165, 233, 0.02)' }
                            }}
                        >
                            <input {...getInputProps()} />
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: {xs: 1, md: 2}, mb: 1.5, color: isDragActive ? 'primary.main' : 'text.secondary' }}>
                                <CloudUploadIcon sx={{ fontSize: {xs: 32, md: 36} }} />
                                <FolderZipIcon sx={{ fontSize: {xs: 32, md: 36} }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isDragActive ? "primary.main" : "text.primary", fontSize: {xs: '0.95rem', md: '1rem'} }}>
                                {t.dropText}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: {xs: '0.75rem', md: '0.875rem'} }}>
                                {t.acceptTypes}
                            </Typography>
                        </Paper>

                        {configInfo && (
                            <Box sx={{ mb: {xs: 3, md: 2.5} }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, p: {xs: 1.5, md: 1.5}, borderRadius: 2, bgcolor: mode === 'dark' ? 'rgba(56, 189, 248, 0.1)' : 'rgba(14, 165, 233, 0.05)', border: '1px solid', borderColor: mode === 'dark' ? 'rgba(56, 189, 248, 0.2)' : 'rgba(14, 165, 233, 0.2)' }}>
                                    <SettingsInputComponentIcon color="primary" sx={{ fontSize: {xs: 18, md: 20} }} />
                                    <Typography variant="body2" fontWeight="bold" sx={{ color: 'primary.main', fontSize: {xs: '0.8rem', md: '0.85rem'} }}>
                                        {t.configInfo}
                                    </Typography>
                                    <Chip label={configInfo.proto} size="small" sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: 20 }} />
                                    {configInfo.remote && <Chip label={configInfo.remote} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />}
                                    {configInfo.port && <Chip label={configInfo.port} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />}
                                    {configInfo.cipher && <Chip label={configInfo.cipher} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />}
                                </Box>
                                {!configInfo.isClient && (
                                    <Alert severity="warning" sx={{ mt: 1, borderRadius: 2, py: 0, px: 2, '& .MuiAlert-message': {fontSize: '0.8rem'} }}>
                                        {t.notClient}
                                    </Alert>
                                )}
                            </Box>
                        )}

                        <Grid container spacing={{xs: 2, md: 3}}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: {xs: 1, md: 1}, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, fontSize: {xs: '0.75rem', md: '0.8rem'} }}>
                                    {t.reqSection}
                                </Typography>
                                <FileRow type="ovpn" isRequired={true} />
                                <FileRow type="ca" isRequired={true} />
                                <FileRow type="cert" isRequired={true} />
                                <FileRow type="key" isRequired={true} />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: {xs: 1, md: 1}, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, fontSize: {xs: '0.75rem', md: '0.8rem'} }}>
                                    {t.optSection}
                                </Typography>
                                <FileRow type="ta" isRequired={false} />
                                <FileRow type="dh" isRequired={false} />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Unified Bottom Action Bar */}
                    <Box sx={{ 
                        mt: {xs: 2, md: 3}, 
                        p: {xs: 2, sm: 2, md: 2.5}, 
                        bgcolor: mode === 'dark' ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.02)',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <TextField 
                            size="small"
                            label={t.fileNameLabel}
                            value={customFileName}
                            onChange={(e) => setCustomFileName(e.target.value)}
                            disabled={!files.ovpn}
                            sx={{ width: '100%' }}
                            variant="outlined"
                        />
                        
                        <Box sx={{ display: 'flex', gap: {xs: 1, md: 2}, width: '100%' }}>
                            <Tooltip title={t.resetBtn}>
                                <IconButton onClick={resetAll} color="inherit" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                    <DeleteSweepIcon />
                                </IconButton>
                            </Tooltip>
                            
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={handlePreview}
                                disabled={!isReady}
                                startIcon={<VisibilityIcon />}
                                sx={{ borderRadius: 2, flex: 1, fontWeight: 'bold', fontSize: {xs: '0.8rem', md: '0.875rem'} }}
                            >
                                {t.previewBtn}
                            </Button>
                            
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!isReady}
                                startIcon={<DownloadIcon />}
                                onClick={handleDownload}
                                sx={{ borderRadius: 2, flex: 1, fontWeight: 'bold', boxShadow: 'none', fontSize: {xs: '0.8rem', md: '0.875rem'} }}
                            >
                                {t.downloadBtn}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Container>

            {/* Preview Modal */}
            <Dialog 
                open={previewOpen} 
                onClose={() => setPreviewOpen(false)} 
                maxWidth="md" 
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, bgcolor: 'background.paper' } }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="700">Попередній перегляд .ovpn</Typography>
                    <IconButton onClick={() => setPreviewOpen(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: mode === 'dark' ? '#0a0a0a' : '#f8fafc' }}>
                    <TextField
                        multiline
                        fullWidth
                        value={generatedConfig}
                        onChange={(e) => setGeneratedConfig(e.target.value)}
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            sx: { fontFamily: 'monospace', fontSize: '0.85rem', p: 3, lineHeight: 1.5, color: mode === 'dark' ? '#a5b4fc' : '#334155' }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Tooltip title={copied ? t.copied : ""}>
                        <Button 
                            color="inherit" 
                            startIcon={<ContentCopyIcon />} 
                            onClick={handleCopy}
                            sx={{ mr: 'auto' }}
                        >
                            {copied ? t.copied : t.copyBtn}
                        </Button>
                    </Tooltip>
                    <Button onClick={() => setPreviewOpen(false)} color="inherit">Отмена</Button>
                    <Button onClick={handleDownload} variant="contained" color="primary" startIcon={<DownloadIcon />}>
                        {t.downloadBtn}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Global Toasts */}
            <Snackbar open={toast.open} autoHideDuration={4000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
